import React, { useState, useEffect } from 'react';
import { 
  Server, 
  Play, 
  Square, 
  Trash2, 
  Share2, 
  AlertTriangle, 
  CheckCircle, 
  Loader,
  ExternalLink,
  Users,
  HardDrive,
  SquareTerminal,
  Cpu,
  Layers2,
  Box,
  Plus
} from 'lucide-react';
import BlenderServerManager from '../../components/modals/BlenderServerManager';
import FreeCADServerManager from '../../components/modals/FreeCADServerManager';
import JupyterLabServerManager from '../../components/modals/JupyterLabServerManager';
import KiCadServerManager from '../../components/modals/KiCadServerManager';
import StorageManager from '../../components/modals/StorageManager';
import styles from './ServersSettings.module.css';

// Optional ViewerContext usage
let useViewerContext;
try {
  const viewerContext = require('../../context/ViewerContext');
  useViewerContext = viewerContext.useViewerContext;
} catch (error) {
  // Fallback when ViewerContext is not available
  useViewerContext = () => ({ openFile: () => {} });
}

export default function ServersSettings() {
  const [activeTab, setActiveTab] = useState('blender');
  const [lastServerTab, setLastServerTab] = useState('blender'); // Track last active server tab
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedServer, setSelectedServer] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTenantId, setShareTenantId] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  
  // Safely get openFile from context
  let openFile;
  try {
    const viewerContext = useViewerContext();
    openFile = viewerContext.openFile;
  } catch (error) {
    // Fallback when context is not available
    openFile = () => {};
  }

  // Update last server tab when switching between server tabs
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (newTab === 'blender' || newTab === 'freecad' || newTab === 'jupyterlab' || newTab === 'kicad') {
      setLastServerTab(newTab);
    }
  };

  // Fetch servers on component mount
  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('/v1/eks_jupyterlab/list', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        setServers(data.data.servers || []);
      } else {
        setError(data.retmsg || 'Failed to fetch servers');
      }
    } catch (err) {
      console.error('Error fetching servers:', err);
      setError('Failed to fetch servers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const createServer = async () => {
    setActionLoading({ ...actionLoading, create: true });
    setError('');
    
    try {
      const response = await fetch('/v1/eks_jupyterlab/create', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          server_name: `server-${Date.now()}`
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        await fetchServers(); // Refresh the list
      } else {
        setError(data.retmsg || 'Failed to create server');
      }
    } catch (err) {
      console.error('Error creating server:', err);
      setError('Failed to create server. Please try again.');
    } finally {
      setActionLoading({ ...actionLoading, create: false });
    }
  };

  const startServer = async (serverName) => {
    setActionLoading({ ...actionLoading, [serverName]: true });
    setError('');
    
    try {
      const response = await fetch(`/v1/eks_jupyterlab/start/${serverName}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        await fetchServers(); // Refresh the list
      } else {
        setError(data.retmsg || 'Failed to start server');
      }
    } catch (err) {
      console.error('Error starting server:', err);
      setError('Failed to start server. Please try again.');
    } finally {
      setActionLoading({ ...actionLoading, [serverName]: false });
    }
  };

  const stopServer = async (serverName) => {
    setActionLoading({ ...actionLoading, [serverName]: true });
    setError('');
    
    try {
      const response = await fetch(`/v1/eks_jupyterlab/stop/${serverName}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        await fetchServers(); // Refresh the list
      } else {
        setError(data.retmsg || 'Failed to stop server');
      }
    } catch (err) {
      console.error('Error stopping server:', err);
      setError('Failed to stop server. Please try again.');
    } finally {
      setActionLoading({ ...actionLoading, [serverName]: false });
    }
  };

  const deleteServer = async (serverName) => {
    if (!confirm(`Are you sure you want to delete server "${serverName}"? This will permanently delete all data.`)) {
      return;
    }

    setActionLoading({ ...actionLoading, [serverName]: true });
    setError('');
    
    try {
      const response = await fetch(`/v1/eks_jupyterlab/delete/${serverName}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        await fetchServers(); // Refresh the list
      } else {
        setError(data.retmsg || 'Failed to delete server');
      }
    } catch (err) {
      console.error('Error deleting server:', err);
      setError('Failed to delete server. Please try again.');
    } finally {
      setActionLoading({ ...actionLoading, [serverName]: false });
    }
  };

  const shareServer = async (serverName) => {
    if (!shareTenantId.trim()) {
      setError('Please enter a user ID to share with');
      return;
    }

    setActionLoading({ ...actionLoading, [serverName]: true });
    setError('');
    
    try {
      const response = await fetch(`/v1/eks_jupyterlab/share/${serverName}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          target_user_id: shareTenantId.trim()
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        setShowShareModal(false);
        setShareTenantId('');
        await fetchServers(); // Refresh the list
      } else {
        setError(data.retmsg || 'Failed to share server');
      }
    } catch (err) {
      console.error('Error sharing server:', err);
      setError('Failed to share server. Please try again.');
    } finally {
      setActionLoading({ ...actionLoading, [serverName]: false });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'text-green-500';
      case 'stopped':
        return 'text-yellow-500';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'stopped':
        return <Square className="h-4 w-4 text-yellow-500" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Server className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const renderTabBar = () => (
    <div className={styles.tabBar}>
      <button
        onClick={() => handleTabChange('blender')}
        className={
          activeTab === 'blender'
            ? `${styles.tabButton} ${styles.tabButtonActive}`
            : styles.tabButton
        }
      >
        <Layers2 className="h-5 w-5 mr-2" />
        Blender
      </button>
      <button
        onClick={() => handleTabChange('freecad')}
        className={
          activeTab === 'freecad'
            ? `${styles.tabButton} ${styles.tabButtonActive}`
            : styles.tabButton
        }
      >
        <Box className="h-5 w-5 mr-2" />
        FreeCAD
      </button>
      <button
        onClick={() => handleTabChange('jupyterlab')}
        className={
          activeTab === 'jupyterlab'
            ? `${styles.tabButton} ${styles.tabButtonActive}`
            : styles.tabButton
        }
      >
        <SquareTerminal className="h-5 w-5 mr-2" />
        JupyterLab
      </button>
      <button
        onClick={() => handleTabChange('kicad')}
        className={
          activeTab === 'kicad'
            ? `${styles.tabButton} ${styles.tabButtonActive}`
            : styles.tabButton
        }
      >
        <Cpu className="h-5 w-5 mr-2" />
        KiCad
      </button>
      <button
        onClick={() => handleTabChange('storage')}
        className={
          activeTab === 'storage'
            ? `${styles.tabButton} ${styles.tabButtonActive}`
            : styles.tabButton
        }
      >
        <HardDrive className="h-5 w-5 mr-2" />
        Storage
      </button>
    </div>
  );

  const getTabDescription = () => {
    switch (activeTab) {
      case 'blender':
        return 'Create, start, stop, share or delete your Blender servers.';
      case 'freecad':
        return 'Create, start, stop, share or delete your FreeCAD servers.';
      case 'jupyterlab':
        return 'Create, start, stop, share or delete your JupyterLab servers.';
      case 'kicad':
        return 'Create, start, stop, share or delete your KiCad servers.';
      case 'storage':
        return 'Manage your persistent storage independently of servers.';
      default:
        return 'Manage your development servers.';
    }
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case 'blender':
        return 'Blender Servers';
      case 'freecad':
        return 'FreeCAD Servers';
      case 'jupyterlab':
        return 'JupyterLab Servers';
      case 'kicad':
        return 'KiCad Servers';
      case 'storage':
        return 'Storage Management';
      default:
        return 'Development Servers';
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading servers...</p>
      </div>
    );
  }

  return (
    <div className={styles.serversSettings}>
      <div className={styles.header}>
        <div>
          <h1>Server Management</h1>
          <p className={styles.subtitle}>Manage your development servers and storage</p>
        </div>
      </div>

      <div className={styles.content}>
        {renderTabBar()}
        
        <p className={styles.info}>
          {getTabDescription()}
        </p>

        {error && (
          <div className={styles.errorAlert}>
            {error}
          </div>
        )}

        {/* Tab content */}
        <div className={styles.serverList}>
          {activeTab === 'blender' && (
            <BlenderServerManager />
          )}
          {activeTab === 'freecad' && (
            <FreeCADServerManager />
          )}
          {activeTab === 'jupyterlab' && (
            <JupyterLabServerManager />
          )}
          {activeTab === 'kicad' && (
            <KiCadServerManager />
          )}
          {activeTab === 'storage' && (
            <StorageManager appType="jupyterlab" />
          )}
        </div>
      </div>
    </div>
  );
}
